/**
 * Web LiveKit Transport for Pipecat (admin app)
 *
 * Connects to a LiveKit room via livekit-client (browser) and bridges
 * audio/data with PipecatClient.  Replaces CustomWebRTCTransport for the
 * episode try-flow in the admin app.
 *
 * Audio is published/subscribed automatically by LiveKit.
 * RTVI messages flow through LiveKit's reliable data channel.
 */

import { RTVIMessage, Transport, TransportStartError } from '@pipecat-ai/client-js';
import type { PipecatClientOptions, Tracks, TransportState } from '@pipecat-ai/client-js';
import {
  Room,
  RoomEvent,
  RemoteParticipant,
  RemoteTrackPublication,
  Track,
  ConnectionState,
  DisconnectReason,
} from 'livekit-client';
import logger from 'utils/logger';

export interface WebLiveKitTransportOptions {
  /** LiveKit server WebSocket URL (e.g. wss://lk.klaro.name) */
  url: string;
  /** JWT token for the client participant */
  token: string;
  /** Room name to join */
  roomName: string;
}

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();

const BOT_JOIN_TIMEOUT_MS = 15_000;

export class WebLiveKitTransport extends Transport {
  public static SERVICE_NAME = 'livekit-transport-web';

  private _url: string;
  private _token: string;
  private _roomName: string;
  private _room: Room | null = null;
  private _micEnabled: boolean = true;
  private _awaitingBotReady: boolean = false;
  private _botJoinTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: WebLiveKitTransportOptions) {
    super();
    this._state = 'disconnected';
    this._url = opts.url;
    this._token = opts.token;
    this._roomName = opts.roomName;
  }

  // ── PipecatClient lifecycle ───────────────────────────────────────

  public initialize(
    options: PipecatClientOptions,
    messageHandler: (ev: RTVIMessage) => void
  ): void {
    this._options = options;
    this._callbacks = options.callbacks ?? {};
    this._onMessage = messageHandler;
    this.state = 'disconnected';
    logger.debug('[LiveKit Web Transport] Initialized');
  }

  async initDevices(): Promise<void> {
    this.state = 'initializing';
    this.state = 'initialized';
  }

  _validateConnectionParams(connectParams: unknown): Record<string, unknown> | undefined {
    if (
      connectParams &&
      typeof connectParams === 'object' &&
      Object.keys(connectParams).length > 0
    ) {
      return connectParams as Record<string, unknown>;
    }
    return undefined;
  }

  async _connect(): Promise<void> {
    if (this._abortController?.signal.aborted) return;

    this.state = 'connecting';

    const url = this._url;
    const token = this._token;

    if (!url || !token) {
      logger.error('[LiveKit Web Transport] Missing url or token');
      this.state = 'error';
      throw new TransportStartError();
    }

    const room = new Room();
    this._room = room;

    room.on(RoomEvent.DataReceived, this._onDataReceived);
    room.on(RoomEvent.Disconnected, this._onRoomDisconnected);
    room.on(RoomEvent.ParticipantConnected, this._onParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, this._onParticipantDisconnected);
    room.on(RoomEvent.TrackSubscribed, this._onTrackSubscribed);

    try {
      logger.info(`[LiveKit Web Transport] Connecting to ${url} room=${this._roomName}`);
      await room.connect(url, token);
      logger.info('[LiveKit Web Transport] Connected to LiveKit room');
    } catch (err) {
      logger.error('[LiveKit Web Transport] Connection failed:', err);
      this.state = 'error';
      this._cleanup();
      throw new TransportStartError();
    }

    if (this._abortController?.signal.aborted) {
      this._cleanup();
      return;
    }

    try {
      await room.localParticipant.setMicrophoneEnabled(this._micEnabled);
      logger.info('[LiveKit Web Transport] Microphone published');
    } catch (err) {
      logger.warn('[LiveKit Web Transport] Failed to publish microphone:', err);
    }

    this.state = 'connected';
    this._callbacks.onConnected?.();

    // Start bot-join watchdog. If no bot-* participant joins within the
    // timeout the pipeline likely failed to start — surface as a disconnect.
    if (!this._hasBotParticipant()) {
      this._botJoinTimeoutId = setTimeout(() => {
        logger.error('[LiveKit Web Transport] Bot did not join within 15s — aborting');
        this._cleanup();
        this._callbacks.onDisconnected?.();
      }, BOT_JOIN_TIMEOUT_MS);
    }
  }

  async _disconnect(): Promise<void> {
    logger.info('[LiveKit Web Transport] Disconnecting');
    this._cleanup();
  }

  // ── Messaging ─────────────────────────────────────────────────────

  sendReadyMessage(): void {
    this.state = 'ready';
    this._awaitingBotReady = true;

    // Send client_ready immediately (optimistic — covers bot-first case).
    // If bot hasn't joined yet the message is lost, which is fine: the bot
    // will send bot-pipeline-ready when its pipeline is initialized, and
    // _onDataReceived will resend client_ready at that point.
    logger.info('[LiveKit Web Transport] Sending client_ready (optimistic)');
    this.sendMessage(RTVIMessage.clientReady());
  }

  sendMessage(message: RTVIMessage): void {
    const room = this._room;
    if (!room || room.state !== ConnectionState.Connected) {
      logger.warn(`[LiveKit Web Transport] Room not connected. Message not sent: ${message.type}`);
      return;
    }
    const payload = TEXT_ENCODER.encode(JSON.stringify(message));
    room.localParticipant.publishData(payload, { reliable: true }).catch((err) => {
      logger.warn('[LiveKit Web Transport] Failed to send data message:', err);
    });
  }

  // ── Media controls ────────────────────────────────────────────────

  enableMic(enable: boolean): void {
    this._micEnabled = enable;
    this._room?.localParticipant
      .setMicrophoneEnabled(enable)
      .catch((err) => logger.warn('[LiveKit Web Transport] setMicrophoneEnabled failed:', err));
  }

  enableCam(_enable: boolean): void {}
  enableScreenShare(_enable: boolean): void {}

  get isMicEnabled(): boolean {
    return this._micEnabled;
  }
  get isCamEnabled(): boolean {
    return false;
  }
  get isSharingScreen(): boolean {
    return false;
  }

  // ── Device enumeration (stubs) ────────────────────────────────────

  async getAllMics(): Promise<MediaDeviceInfo[]> {
    return [];
  }
  async getAllCams(): Promise<MediaDeviceInfo[]> {
    return [];
  }
  async getAllSpeakers(): Promise<MediaDeviceInfo[]> {
    return [];
  }
  updateMic(_micId: string): void {}
  updateCam(_camId: string): void {}
  updateSpeaker(_speakerId: string): void {}
  get selectedMic(): MediaDeviceInfo | Record<string, never> {
    return {};
  }
  get selectedCam(): MediaDeviceInfo | Record<string, never> {
    return {};
  }
  get selectedSpeaker(): MediaDeviceInfo | Record<string, never> {
    return {};
  }

  // ── State & tracks ────────────────────────────────────────────────

  get state(): TransportState {
    return this._state;
  }

  set state(state: TransportState) {
    this._state = state;
  }

  tracks(): Tracks {
    return {
      local: {
        audio: undefined,
        video: undefined,
        screenVideo: undefined,
        screenAudio: undefined,
      },
      bot: {
        audio: undefined,
        video: undefined,
        screenVideo: undefined,
        screenAudio: undefined,
      },
    };
  }

  // ── Event handlers ────────────────────────────────────────────────

  private _onDataReceived = (payload: Uint8Array): void => {
    try {
      const text = TEXT_DECODER.decode(payload);
      const parsed = JSON.parse(text);

      // Bot signals its pipeline is ready — resend client_ready now that
      // the RTVI processor can handle it (covers the common client-first case).
      if (parsed.type === 'bot-pipeline-ready' && this._awaitingBotReady) {
        this._awaitingBotReady = false;
        logger.info('[LiveKit Web Transport] Received bot-pipeline-ready, resending client_ready');
        this.sendMessage(RTVIMessage.clientReady());
        return;
      }

      const message = parsed as RTVIMessage;
      this._onMessage(message);
    } catch (err) {
      logger.warn('[LiveKit Web Transport] Failed to parse data message:', err);
    }
  };

  private _onRoomDisconnected = (reason?: DisconnectReason): void => {
    logger.info(`[LiveKit Web Transport] Room disconnected (reason=${reason ?? 'unknown'})`);
    if (this._state === 'disconnected') return; // Already handled by bot participant leaving
    this.state = 'disconnected';
    this._callbacks.onDisconnected?.();
  };

  private _onTrackSubscribed = (
    track: RemoteTrackPublication['track'],
    _publication: RemoteTrackPublication,
    participant: RemoteParticipant
  ): void => {
    if (track && track.kind === Track.Kind.Audio) {
      // Attach the remote audio track to a DOM element so the browser plays it.
      // livekit-client does NOT auto-play audio in browsers — explicit attachment required.
      const el = track.attach();
      el.id = `lk-audio-${participant.identity}`;
      document.body.appendChild(el);
      logger.info(`[LiveKit Web Transport] Audio track attached for ${participant.identity}`);
    }
  };

  private _onParticipantConnected = (participant: RemoteParticipant): void => {
    logger.info(`[LiveKit Web Transport] Participant connected: ${participant.identity}`);
    if (participant.identity.startsWith('bot-') && this._botJoinTimeoutId !== null) {
      clearTimeout(this._botJoinTimeoutId);
      this._botJoinTimeoutId = null;
      logger.info('[LiveKit Web Transport] Bot joined — watchdog cleared');
    }
  };

  private _onParticipantDisconnected = (participant: RemoteParticipant): void => {
    logger.info(`[LiveKit Web Transport] Participant disconnected: ${participant.identity}`);
    if (participant.identity.startsWith('bot-')) {
      logger.info('[LiveKit Web Transport] Bot participant left — signaling disconnect');
      this.state = 'disconnected';
      this._callbacks.onDisconnected?.();
      this._cleanup();
    }
  };

  // ── Cleanup ───────────────────────────────────────────────────────

  private _hasBotParticipant(): boolean {
    return Array.from(this._room?.remoteParticipants.values() ?? []).some((p) =>
      p.identity.startsWith('bot-')
    );
  }

  private _cleanup(): void {
    if (this._botJoinTimeoutId !== null) {
      clearTimeout(this._botJoinTimeoutId);
      this._botJoinTimeoutId = null;
    }
    this._awaitingBotReady = false;
    const room = this._room;
    if (room) {
      room.off(RoomEvent.DataReceived, this._onDataReceived);
      room.off(RoomEvent.Disconnected, this._onRoomDisconnected);
      room.off(RoomEvent.ParticipantConnected, this._onParticipantConnected);
      room.off(RoomEvent.ParticipantDisconnected, this._onParticipantDisconnected);
      room.off(RoomEvent.TrackSubscribed, this._onTrackSubscribed);
      // Remove attached audio elements
      room.remoteParticipants.forEach((p) => {
        const el = document.getElementById(`lk-audio-${p.identity}`);
        el?.remove();
      });
      room.disconnect().catch(() => {});
      this._room = null;
    }
    this.state = 'disconnected';
  }
}
